import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  type ReactElement,
  type ReactNode,
} from 'react';

import { testIdAttr, testIdPart } from '@/lib/testIds';

/** The shape of the MUI input this wraps, as far as Field needs to know. */
interface WrappedInputProps {
  id?: string;
  label?: string;
  error?: boolean;
  /** True for a dropdown, whose clickable part is not its input. */
  select?: boolean;
  children?: ReactNode;
  slotProps?: {
    htmlInput?: Record<string, unknown>;
    select?: { SelectDisplayProps?: object } & Record<string, unknown>;
  };
}

interface FieldProps {
  label: string;
  /** Shown under the control when validation fails. */
  error?: string;
  /** Extra guidance shown under the control. */
  hint?: string;
  /**
   * Names the field for the testing team's tests. A text box gets
   * `<testId>-input`; a dropdown gets `<testId>-select` and each of its
   * options `<testId>-option-<value>`. The hint and the error get `-hint` and
   * `-error`. See docs/test-ids.md.
   */
  testId: string;
  /** A MUI input, select or textarea. Receives the label and aria attributes. */
  children: ReactElement<WrappedInputProps>;
}

/**
 * Wraps one form control with its label, hint and error message, and wires up
 * the accessibility attributes that connect them.
 *
 * Doing this in one component is what makes the WCAG 2.1 AA requirement
 * testable: every field in the portal then announces its own error to a screen
 * reader, instead of showing red text that only sighted users notice.
 *
 * Note that the aria attributes go through `slotProps.htmlInput`. Props spread
 * directly onto a MUI input land on its outer wrapper, not on the `<input>`
 * itself, where a screen reader would never see them.
 *
 * The test ID goes on the part a test acts on, for the same reason. On a text
 * box that is the `<input>`, so Playwright's `fill()` works on it. On a
 * dropdown the `<input>` is hidden and the clickable part is a separate
 * `role="combobox"` element, reached through `SelectDisplayProps`. The options
 * are tagged here too, so no form has to remember them.
 */
export function Field({ label, error, hint, testId, children }: FieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  const childProps = children.props;
  const select = Boolean(childProps.select);

  return (
    <FormControl error={Boolean(error)} fullWidth>
      {cloneElement(children, {
        id,
        label,
        error: Boolean(error),
        slotProps: {
          ...childProps.slotProps,
          htmlInput: {
            'aria-describedby': describedBy || undefined,
            ...(select ? {} : testIdAttr(`${testId}-input`)),
            ...childProps.slotProps?.htmlInput,
          },
          ...(select
            ? {
                select: {
                  ...childProps.slotProps?.select,
                  SelectDisplayProps: {
                    ...testIdAttr(`${testId}-select`),
                    ...childProps.slotProps?.select?.SelectDisplayProps,
                  },
                },
              }
            : {}),
        },
        ...(select
          ? {
              children: Children.map(childProps.children, (option) =>
                isValidElement<{ value?: string | number }>(option)
                  ? cloneElement(
                      option,
                      testIdAttr(
                        `${testId}-option-${testIdPart(option.props.value ?? '')}`,
                      ),
                    )
                  : option,
              ),
            }
          : {}),
      })}

      {hint ? (
        <FormHelperText id={hintId} data-testid={`${testId}-hint`}>
          {hint}
        </FormHelperText>
      ) : null}

      {error ? (
        // role="alert" makes a screen reader announce the message as soon as it
        // appears, rather than only when the person happens to reach the field.
        <FormHelperText
          id={errorId}
          role="alert"
          data-testid={`${testId}-error`}
          sx={{ fontWeight: 500 }}
        >
          {error}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
}
